import { Clock, Settings } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import type { WorkerDetailFragment } from "@/gql/graphql";
import { Can } from "@/lib/authorization";
import { ColorUtils } from "@/lib/utils/color-utils";
import { getWorkerDisplayName, getWorkerInitials } from "../../utils/workers-utils";

interface WorkerCardProps {
  worker: WorkerDetailFragment;
  onEditWorker: (worker: WorkerDetailFragment) => void;
  onToggleOnlineBooking: (worker: WorkerDetailFragment) => void;
  onDeactivateWorker: (worker: WorkerDetailFragment) => void;
  isTogglingOnlineBooking?: boolean;
  isDeactivating?: boolean;
}

export function WorkerCard({
  worker,
  onEditWorker,
  onToggleOnlineBooking,
  onDeactivateWorker,
  isTogglingOnlineBooking = false,
  isDeactivating = false,
}: WorkerCardProps) {
  const workerColor = ColorUtils.getColorStyle(worker.color);
  const workerDisplayName = getWorkerDisplayName(worker);
  const workerInitials = getWorkerInitials(worker);

  return (
    <Card className="group hover:shadow-md transition-all duration-200 bg-card border border-border hover:border-border/80">
      <CardContent className="p-4 sm:p-6">
        {/* Mobile Layout */}
        <div className="lg:hidden">
          <div className="space-y-4">
            {/* Top Row: Worker Profile */}
            <div className="flex items-start gap-3">
              <div className="relative flex-shrink-0">
                <Avatar className="w-14 h-14 border-2 border-background shadow-sm">
                  <AvatarImage src={worker.profileImageUrl ?? undefined} />
                  <AvatarFallback
                    className={`${workerColor.className} text-white font-semibold`}
                    style={workerColor.style}
                  >
                    {workerInitials}
                  </AvatarFallback>
                </Avatar>
                <div
                  className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-background ${
                    worker.isActive ? "bg-green-500" : "bg-muted-foreground"
                  }`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground text-lg truncate">
                  {workerDisplayName}
                </h3>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground truncate">
                    {worker.user?.email || "No Email"}
                  </span>
                  {worker.user?.role && worker.user.role.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {worker.user.role}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex-shrink-0">
                <Can I="update" a="Worker">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditWorker(worker)}
                    className="h-8 w-8 p-0 hover:bg-accent hover:text-accent-foreground"
                    title="Edit Worker"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </Can>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">
                  <Clock className="w-4 h-4 text-primary inline" /> Status
                </div>
                <div className="font-semibold text-sm">
                  {worker.isActive ? "Active" : "Inactive"}
                </div>
              </div>
            </div>

            {/* Active & Online Booking Toggles */}
            <div className="grid grid-cols-2 gap-3">
              <Can I="update" a="Worker" passThrough>
                {(allowed) =>
                  allowed ? (
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                      <span className="text-xs font-medium text-foreground">Active</span>
                      <div className="relative">
                        <Switch
                          checked={worker.isActive ?? undefined}
                          onCheckedChange={() => onDeactivateWorker(worker)}
                          disabled={isDeactivating}
                          className={`transition-opacity ${
                            isDeactivating ? "opacity-50" : "opacity-100"
                          }`}
                        />
                        {isDeactivating && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border opacity-50">
                      <span className="text-xs font-medium text-foreground">Active</span>
                      <div className="text-xs text-muted-foreground">
                        {worker.isActive ? "Active" : "Inactive"}
                      </div>
                    </div>
                  )
                }
              </Can>
              <Can I="update" a="Worker" passThrough>
                {(allowed) =>
                  allowed ? (
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                      <span className="text-xs font-medium text-foreground">Online</span>
                      <div className="relative">
                        <Switch
                          checked={worker.canAcceptOnlineBookings ?? undefined}
                          onCheckedChange={() => onToggleOnlineBooking(worker)}
                          disabled={isTogglingOnlineBooking}
                          className={`transition-opacity ${
                            isTogglingOnlineBooking ? "opacity-50" : "opacity-100"
                          }`}
                        />
                        {isTogglingOnlineBooking && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border opacity-50">
                      <span className="text-xs font-medium text-foreground">Online</span>
                      <div className="text-xs text-muted-foreground">
                        {worker.canAcceptOnlineBookings ? "Enabled" : "Disabled"}
                      </div>
                    </div>
                  )
                }
              </Can>
            </div>
          </div>
        </div>

        {/* Desktop & Tablet Layout */}
        <div className="hidden lg:block">
          <div className="space-y-4">
            {/* Top Row: Worker Profile & Controls */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="w-16 h-16 border-2 border-background shadow-sm">
                    <AvatarImage src={worker.profileImageUrl ?? undefined} />
                    <AvatarFallback
                      className={`${workerColor.className} text-white font-semibold text-lg`}
                      style={workerColor.style}
                    >
                      {workerInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-background ${
                      worker.isActive ? "bg-green-500" : "bg-muted-foreground"
                    }`}
                  />
                </div>

                <div>
                  <h3 className="font-bold text-foreground text-xl">{workerDisplayName}</h3>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-sm text-muted-foreground">
                      {worker.user?.email || "No Email"}
                    </span>
                    {worker.user?.role && worker.user.role.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {worker.user.role}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Active & Online Booking Controls */}
              <div className="flex items-center gap-6">
                <Can I="update" a="Worker">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditWorker(worker)}
                    className="h-9 w-9 p-0 hover:bg-accent hover:text-accent-foreground"
                    title="Edit Worker"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </Can>

                <div className="flex items-center gap-4">
                  {/* Active Toggle */}
                  <Can I="update" a="Worker" passThrough>
                    {(allowed) =>
                      allowed ? (
                        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border">
                          <span className="text-xs text-muted-foreground font-medium">Active</span>
                          <div className="relative">
                            <Switch
                              checked={worker.isActive ?? undefined}
                              onCheckedChange={() => onDeactivateWorker(worker)}
                              disabled={isDeactivating}
                              className={`transition-opacity ${
                                isDeactivating ? "opacity-50" : "opacity-100"
                              }`}
                            />
                            {isDeactivating && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border opacity-50">
                          <span className="text-xs text-muted-foreground font-medium">Active</span>
                          <span className="text-xs text-muted-foreground">
                            {worker.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      )
                    }
                  </Can>

                  {/* Online Booking Toggle */}
                  <Can I="update" a="Worker" passThrough>
                    {(allowed) =>
                      allowed ? (
                        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border">
                          <span className="text-xs text-muted-foreground font-medium">Online</span>
                          <div className="relative">
                            <Switch
                              checked={worker.canAcceptOnlineBookings ?? undefined}
                              onCheckedChange={() => onToggleOnlineBooking(worker)}
                              disabled={isTogglingOnlineBooking}
                              className={`transition-opacity ${
                                isTogglingOnlineBooking ? "opacity-50" : "opacity-100"
                              }`}
                            />
                            {isTogglingOnlineBooking && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border opacity-50">
                          <span className="text-xs text-muted-foreground font-medium">Online</span>
                          <span className="text-xs text-muted-foreground">
                            {worker.canAcceptOnlineBookings ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                      )
                    }
                  </Can>
                </div>
              </div>
            </div>

            {/* Second Row: Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 bg-card rounded-lg border border-border">
                <div className="text-xs text-muted-foreground mb-1">Joined</div>
                <div className="text-sm font-semibold text-foreground">
                  {worker.createdAt ? new Date(worker.createdAt).toLocaleDateString() : "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
